/**
 * Simple ZIP file creation utility
 */
import { UTF8_BOM } from './csvDownloader'

export function createAndDownloadZip(
  files: Array<{ filename: string; content: string }>,
  zipFilename: string,
): void {
  const zipData = createSimpleZipFile(files)
  const blob = new Blob([zipData.slice()], { type: 'application/zip' })

  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = zipFilename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

function createSimpleZipFile(
  files: Array<{ filename: string; content: string }>,
): Uint8Array {
  const zipParts: Uint8Array[] = []
  const centralDirectory: Uint8Array[] = []
  let offset = 0

  for (const file of files) {
    // Add UTF-8 BOM for CSV files to prevent character encoding issues on Windows
    const isCsvFile = file.filename.toLowerCase().endsWith('.csv')
    const contentBytes = new TextEncoder().encode(file.content)
    const fileContent = isCsvFile
      ? new Uint8Array(UTF8_BOM.length + contentBytes.length)
      : contentBytes

    if (isCsvFile) {
      fileContent.set(UTF8_BOM, 0)
      fileContent.set(contentBytes, UTF8_BOM.length)
    }

    const filename = new TextEncoder().encode(file.filename)
    const crc = calculateCRC32(fileContent)

    // Local file header
    const localHeader = createLocalFileHeader(filename, fileContent, crc)
    zipParts.push(localHeader)
    zipParts.push(fileContent)

    // Central directory entry
    const centralEntry = createCentralDirectoryEntry(
      filename,
      fileContent,
      crc,
      offset,
    )
    centralDirectory.push(centralEntry)

    offset += localHeader.length + fileContent.length
  }

  // Add central directory
  zipParts.push(...centralDirectory)

  // End of central directory record
  const centralDirSize = centralDirectory.reduce(
    (sum, entry) => sum + entry.length,
    0,
  )
  const endRecord = createEndOfCentralDirectory(
    files.length,
    centralDirSize,
    offset,
  )
  zipParts.push(endRecord)

  // Combine all parts
  const totalLength = zipParts.reduce((sum, part) => sum + part.length, 0)
  const result = new Uint8Array(totalLength)
  let pos = 0

  for (const part of zipParts) {
    result.set(part, pos)
    pos += part.length
  }

  return result
}

function createLocalFileHeader(
  filename: Uint8Array,
  content: Uint8Array,
  crc: number,
): Uint8Array {
  const header = new Uint8Array(30 + filename.length)
  const view = new DataView(header.buffer)

  view.setUint32(0, 0x04034b50, true) // Local file header signature
  view.setUint16(4, 20, true) // Version needed to extract
  view.setUint16(6, 0, true) // General purpose bit flag
  view.setUint16(8, 0, true) // Compression method (0 = no compression)
  view.setUint16(10, 0, true) // Last mod file time
  view.setUint16(12, 0, true) // Last mod file date
  view.setUint32(14, crc, true) // CRC-32
  view.setUint32(18, content.length, true) // Compressed size
  view.setUint32(22, content.length, true) // Uncompressed size
  view.setUint16(26, filename.length, true) // File name length
  view.setUint16(28, 0, true) // Extra field length

  header.set(filename, 30)
  return header
}

function createCentralDirectoryEntry(
  filename: Uint8Array,
  content: Uint8Array,
  crc: number,
  offset: number,
): Uint8Array {
  const entry = new Uint8Array(46 + filename.length)
  const view = new DataView(entry.buffer)

  view.setUint32(0, 0x02014b50, true) // Central file header signature
  view.setUint16(4, 20, true) // Version made by
  view.setUint16(6, 20, true) // Version needed to extract
  view.setUint16(8, 0, true) // General purpose bit flag
  view.setUint16(10, 0, true) // Compression method
  view.setUint16(12, 0, true) // Last mod file time
  view.setUint16(14, 0, true) // Last mod file date
  view.setUint32(16, crc, true) // CRC-32
  view.setUint32(20, content.length, true) // Compressed size
  view.setUint32(24, content.length, true) // Uncompressed size
  view.setUint16(28, filename.length, true) // File name length
  view.setUint16(30, 0, true) // Extra field length
  view.setUint16(32, 0, true) // File comment length
  view.setUint16(34, 0, true) // Disk number start
  view.setUint16(36, 0, true) // Internal file attributes
  view.setUint32(38, 0, true) // External file attributes
  view.setUint32(42, offset, true) // Relative offset of local header

  entry.set(filename, 46)
  return entry
}

function createEndOfCentralDirectory(
  entryCount: number,
  centralDirSize: number,
  centralDirOffset: number,
): Uint8Array {
  const record = new Uint8Array(22)
  const view = new DataView(record.buffer)

  view.setUint32(0, 0x06054b50, true) // End of central directory signature
  view.setUint16(4, 0, true) // Number of this disk
  view.setUint16(6, 0, true) // Disk where central directory starts
  view.setUint16(8, entryCount, true) // Number of central directory records on this disk
  view.setUint16(10, entryCount, true) // Total number of central directory records
  view.setUint32(12, centralDirSize, true) // Size of central directory
  view.setUint32(16, centralDirOffset, true) // Offset of start of central directory
  view.setUint16(20, 0, true) // ZIP file comment length

  return record
}

function calculateCRC32(data: Uint8Array): number {
  const table: number[] = []
  let crc = 0

  // Generate CRC table
  for (let i = 0; i < 256; i++) {
    crc = i
    for (let j = 0; j < 8; j++) {
      crc = (crc & 1) !== 0 ? 0xedb88320 ^ (crc >>> 1) : crc >>> 1
    }
    table[i] = crc
  }

  // Calculate CRC
  crc = 0xffffffff
  for (let i = 0; i < data.length; i++) {
    crc = table[(crc ^ data[i]) & 0xff] ^ (crc >>> 8)
  }

  return (crc ^ 0xffffffff) >>> 0
}
